// CANONICAL COPY (GH #7115). src/constants/frameworks/iso42001.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// ISO/IEC 42001:2023 — Artificial Intelligence Management System.
// Framework mapping to TCF controls.
//
// Structure:
//   - Main clauses (4.x–10.x)        — AI management system requirements,
//                                       mirroring the HLS used by ISO 27001 etc.
//   - Annex A (normative, 38 ctrls)  — AI-specific controls across objectives
//                                       A.2 policies through A.10 third parties.
//
// Multiple mappings with the same externalId are intentional — the last entry
// per externalId is used for the scfLookup (cross-reference display), while
// all entries contribute to controlToFrameworks and externalRefs.
//
// AI-specific intent that the generic TCF catalogue does not cover is mapped
// onto SCF-AIG-* controls introduced alongside this framework.

const FRAMEWORK_ISO42001 = {
  id: 'fw-iso42001',
  type: 'standard',
  name: 'ISO/IEC 42001:2023',
  shortName: 'ISO 42001',
  abbrev: 'I42k1',
  description:
    'Artificial Intelligence Management System — requirements for establishing, implementing, maintaining and continually improving an AI management system.',
  color: '#8b5cf6',
  version: '2023',
  sectionKeyDepth: 1,
  // Clauses 4-10, plus one section for the whole of Annex A: every Annex A id
  // is "42001-A.x.y", so a depth-1 key collapses the annex into a single group.
  sections: [
    { key: '42001-4', name: 'Clause 4 — Context of the organization' },
    { key: '42001-5', name: 'Clause 5 — Leadership' },
    { key: '42001-6', name: 'Clause 6 — Planning' },
    { key: '42001-7', name: 'Clause 7 — Support' },
    { key: '42001-8', name: 'Clause 8 — Operation' },
    { key: '42001-9', name: 'Clause 9 — Performance evaluation' },
    { key: '42001-10', name: 'Clause 10 — Improvement' },
    { key: '42001-A', name: 'Annex A — AI controls' },
  ],
  mappings: [
    // ── Clause 4 — Context of the organization ──────────────────────────
    { externalId: '42001-4.1', scfId: 'SCF-MSS-001', ref: 'Cl. 4.1' },
    // Determining the needs and expectations of interested parties is a
    // context clause, not a communication activity — it mapped to
    // SCF-MSS-003 (communication and awareness), which no other standard
    // does for the same harmonised clause. ISO 27001 4.2 maps to
    // SCF-MSS-001; ISO 22301 4.2 maps to SCF-MSS-001 + SCF-GOV-009
    // (GH #6844).
    { externalId: '42001-4.2', scfId: 'SCF-MSS-001', ref: 'Cl. 4.2' },
    { primary: true, externalId: '42001-4.2', scfId: 'SCF-GOV-009', ref: 'Cl. 4.2' }, // primary
    { externalId: '42001-4.3', scfId: 'SCF-MSS-001', ref: 'Cl. 4.3' },
    { externalId: '42001-4.4', scfId: 'SCF-MSS-001', ref: 'Cl. 4.4' }, // primary

    // ── Clause 5 — Leadership ───────────────────────────────────────────
    { externalId: '42001-5.1', scfId: 'SCF-GOV-004', ref: 'Cl. 5.1' }, // primary
    { externalId: '42001-5.2', scfId: 'SCF-GOV-001', ref: 'Cl. 5.2' },
    { primary: true, externalId: '42001-5.2', scfId: 'SCF-AIG-001', ref: 'Cl. 5.2' }, // primary
    { externalId: '42001-5.3', scfId: 'SCF-GOV-002', ref: 'Cl. 5.3' },
    { primary: true, externalId: '42001-5.3', scfId: 'SCF-AIG-002', ref: 'Cl. 5.3' }, // primary

    // ── Clause 6 — Planning ─────────────────────────────────────────────
    { externalId: '42001-6.1.1', scfId: 'SCF-RSK-001', ref: 'Cl. 6.1.1' }, // primary
    { externalId: '42001-6.1.2', scfId: 'SCF-RSK-001', ref: 'Cl. 6.1.2' }, // primary
    { externalId: '42001-6.1.3', scfId: 'SCF-RSK-002', ref: 'Cl. 6.1.3' },
    { primary: true, externalId: '42001-6.1.3', scfId: 'SCF-RSK-001', ref: 'Cl. 6.1.3' }, // primary
    { externalId: '42001-6.1.4', scfId: 'SCF-AIG-004', ref: 'Cl. 6.1.4' }, // primary
    { externalId: '42001-6.2', scfId: 'SCF-RSK-002', ref: 'Cl. 6.2' }, // primary
    // Planning of changes to the management system, the harmonised-structure
    // clause — not IT change management. ISO 27001 6.3 and ISO 22301 6.3 both
    // map to SCF-MSS-004, and SCF-DEV-008 is exactly where the legacy id map
    // wrongly sent ISO 27001's 6.3 until GH #6862 (GH #6844).
    { externalId: '42001-6.3', scfId: 'SCF-MSS-004', ref: 'Cl. 6.3' }, // primary

    // ── Clause 7 — Support ──────────────────────────────────────────────
    { externalId: '42001-7.1', scfId: 'SCF-MSS-002', ref: 'Cl. 7.1' },
    { primary: true, externalId: '42001-7.1', scfId: 'SCF-AIG-003', ref: 'Cl. 7.1' }, // primary
    { externalId: '42001-7.2', scfId: 'SCF-PPL-003', ref: 'Cl. 7.2' }, // primary
    { externalId: '42001-7.3', scfId: 'SCF-MSS-003', ref: 'Cl. 7.3' },
    { primary: true, externalId: '42001-7.3', scfId: 'SCF-PPL-003', ref: 'Cl. 7.3' }, // primary
    { externalId: '42001-7.4', scfId: 'SCF-MSS-003', ref: 'Cl. 7.4' }, // primary
    { externalId: '42001-7.5', scfId: 'SCF-GOV-011', ref: 'Cl. 7.5' }, // primary

    // ── Clause 8 — Operation ────────────────────────────────────────────
    { externalId: '42001-8.1', scfId: 'SCF-MSS-004', ref: 'Cl. 8.1' }, // primary
    { externalId: '42001-8.2', scfId: 'SCF-RSK-001', ref: 'Cl. 8.2' }, // primary
    { externalId: '42001-8.3', scfId: 'SCF-RSK-002', ref: 'Cl. 8.3' },
    { primary: true, externalId: '42001-8.3', scfId: 'SCF-RSK-001', ref: 'Cl. 8.3' }, // primary
    { externalId: '42001-8.4', scfId: 'SCF-AIG-004', ref: 'Cl. 8.4' }, // primary

    // ── Clause 9 — Performance evaluation ───────────────────────────────
    { externalId: '42001-9.1', scfId: 'SCF-MSS-005', ref: 'Cl. 9.1' }, // primary
    { externalId: '42001-9.2', scfId: 'SCF-AUD-001', ref: 'Cl. 9.2' }, // primary
    { externalId: '42001-9.3', scfId: 'SCF-MSS-006', ref: 'Cl. 9.3' }, // primary

    // ── Clause 10 — Improvement ─────────────────────────────────────────
    { externalId: '42001-10.1', scfId: 'SCF-MSS-007', ref: 'Cl. 10.1' }, // primary
    { externalId: '42001-10.2', scfId: 'SCF-MSS-007', ref: 'Cl. 10.2' }, // primary

    // ── Annex A.2 — Policies related to AI ──────────────────────────────
    { externalId: '42001-A.2.2', scfId: 'SCF-AIG-001', ref: 'Annex A.2.2' }, // primary
    { externalId: '42001-A.2.3', scfId: 'SCF-GOV-001', ref: 'Annex A.2.3' },
    { primary: true, externalId: '42001-A.2.3', scfId: 'SCF-AIG-001', ref: 'Annex A.2.3' }, // primary
    { externalId: '42001-A.2.4', scfId: 'SCF-AIG-001', ref: 'Annex A.2.4' }, // primary

    // ── Annex A.3 — Internal organization ───────────────────────────────
    { externalId: '42001-A.3.2', scfId: 'SCF-GOV-002', ref: 'Annex A.3.2' },
    { primary: true, externalId: '42001-A.3.2', scfId: 'SCF-AIG-002', ref: 'Annex A.3.2' }, // primary
    { externalId: '42001-A.3.3', scfId: 'SCF-PPL-008', ref: 'Annex A.3.3' },
    { primary: true, externalId: '42001-A.3.3', scfId: 'SCF-AIG-002', ref: 'Annex A.3.3' }, // primary

    // ── Annex A.4 — Resources for AI systems ────────────────────────────
    { externalId: '42001-A.4.2', scfId: 'SCF-GOV-011', ref: 'Annex A.4.2' },
    { primary: true, externalId: '42001-A.4.2', scfId: 'SCF-AIG-003', ref: 'Annex A.4.2' }, // primary
    { externalId: '42001-A.4.3', scfId: 'SCF-AST-001', ref: 'Annex A.4.3' },
    { primary: true, externalId: '42001-A.4.3', scfId: 'SCF-AIG-003', ref: 'Annex A.4.3' }, // primary
    { externalId: '42001-A.4.4', scfId: 'SCF-AST-001', ref: 'Annex A.4.4' },
    { primary: true, externalId: '42001-A.4.4', scfId: 'SCF-AIG-003', ref: 'Annex A.4.4' }, // primary
    { externalId: '42001-A.4.5', scfId: 'SCF-OPS-002', ref: 'Annex A.4.5' },
    { primary: true, externalId: '42001-A.4.5', scfId: 'SCF-AIG-003', ref: 'Annex A.4.5' }, // primary
    { externalId: '42001-A.4.6', scfId: 'SCF-PPL-003', ref: 'Annex A.4.6' },
    { primary: true, externalId: '42001-A.4.6', scfId: 'SCF-AIG-003', ref: 'Annex A.4.6' }, // primary

    // ── Annex A.5 — Assessing impacts of AI systems ─────────────────────
    { externalId: '42001-A.5.2', scfId: 'SCF-AIG-004', ref: 'Annex A.5.2' }, // primary
    { externalId: '42001-A.5.3', scfId: 'SCF-AIG-004', ref: 'Annex A.5.3' }, // primary
    { externalId: '42001-A.5.4', scfId: 'SCF-AIG-004', ref: 'Annex A.5.4' }, // primary
    { externalId: '42001-A.5.5', scfId: 'SCF-AIG-004', ref: 'Annex A.5.5' }, // primary

    // ── Annex A.6 — AI system life cycle ────────────────────────────────
    { externalId: '42001-A.6.1.2', scfId: 'SCF-DEV-001', ref: 'Annex A.6.1.2' },
    { primary: true, externalId: '42001-A.6.1.2', scfId: 'SCF-AIG-005', ref: 'Annex A.6.1.2' }, // primary
    { externalId: '42001-A.6.1.3', scfId: 'SCF-DEV-003', ref: 'Annex A.6.1.3' },
    { primary: true, externalId: '42001-A.6.1.3', scfId: 'SCF-AIG-005', ref: 'Annex A.6.1.3' }, // primary
    { externalId: '42001-A.6.2.2', scfId: 'SCF-DEV-002', ref: 'Annex A.6.2.2' },
    { primary: true, externalId: '42001-A.6.2.2', scfId: 'SCF-AIG-005', ref: 'Annex A.6.2.2' }, // primary
    { externalId: '42001-A.6.2.3', scfId: 'SCF-GOV-011', ref: 'Annex A.6.2.3' },
    { primary: true, externalId: '42001-A.6.2.3', scfId: 'SCF-AIG-008', ref: 'Annex A.6.2.3' }, // primary
    { externalId: '42001-A.6.2.4', scfId: 'SCF-DEV-005', ref: 'Annex A.6.2.4' },
    { primary: true, externalId: '42001-A.6.2.4', scfId: 'SCF-AIG-006', ref: 'Annex A.6.2.4' }, // primary
    { externalId: '42001-A.6.2.5', scfId: 'SCF-DEV-008', ref: 'Annex A.6.2.5' },
    { primary: true, externalId: '42001-A.6.2.5', scfId: 'SCF-AIG-006', ref: 'Annex A.6.2.5' }, // primary
    { externalId: '42001-A.6.2.6', scfId: 'SCF-OPS-007', ref: 'Annex A.6.2.6' },
    { primary: true, externalId: '42001-A.6.2.6', scfId: 'SCF-AIG-007', ref: 'Annex A.6.2.6' }, // primary
    { externalId: '42001-A.6.2.7', scfId: 'SCF-AIG-008', ref: 'Annex A.6.2.7' }, // primary
    { externalId: '42001-A.6.2.8', scfId: 'SCF-OPS-006', ref: 'Annex A.6.2.8' },
    { primary: true, externalId: '42001-A.6.2.8', scfId: 'SCF-AIG-007', ref: 'Annex A.6.2.8' }, // primary

    // ── Annex A.7 — Data for AI systems ─────────────────────────────────
    { externalId: '42001-A.7.2', scfId: 'SCF-AIG-009', ref: 'Annex A.7.2' }, // primary
    { externalId: '42001-A.7.3', scfId: 'SCF-AIG-009', ref: 'Annex A.7.3' }, // primary
    { externalId: '42001-A.7.4', scfId: 'SCF-AIG-009', ref: 'Annex A.7.4' }, // primary
    { externalId: '42001-A.7.5', scfId: 'SCF-AIG-009', ref: 'Annex A.7.5' }, // primary
    { externalId: '42001-A.7.6', scfId: 'SCF-AIG-009', ref: 'Annex A.7.6' }, // primary

    // ── Annex A.8 — Information for interested parties of AI systems ────
    { externalId: '42001-A.8.2', scfId: 'SCF-AIG-008', ref: 'Annex A.8.2' }, // primary
    { externalId: '42001-A.8.3', scfId: 'SCF-AIG-010', ref: 'Annex A.8.3' }, // primary
    { externalId: '42001-A.8.4', scfId: 'SCF-INC-003', ref: 'Annex A.8.4' },
    { primary: true, externalId: '42001-A.8.4', scfId: 'SCF-AIG-010', ref: 'Annex A.8.4' }, // primary
    { externalId: '42001-A.8.5', scfId: 'SCF-AIG-010', ref: 'Annex A.8.5' }, // primary

    // ── Annex A.9 — Use of AI systems ───────────────────────────────────
    { externalId: '42001-A.9.2', scfId: 'SCF-AIG-011', ref: 'Annex A.9.2' }, // primary
    { externalId: '42001-A.9.3', scfId: 'SCF-AIG-011', ref: 'Annex A.9.3' }, // primary
    { externalId: '42001-A.9.4', scfId: 'SCF-AIG-011', ref: 'Annex A.9.4' }, // primary

    // ── Annex A.10 — Third-party and customer relationships ─────────────
    { externalId: '42001-A.10.2', scfId: 'SCF-AIG-012', ref: 'Annex A.10.2' }, // primary
    { externalId: '42001-A.10.3', scfId: 'SCF-VDR-001', ref: 'Annex A.10.3' },
    { primary: true, externalId: '42001-A.10.3', scfId: 'SCF-AIG-012', ref: 'Annex A.10.3' }, // primary
    { externalId: '42001-A.10.4', scfId: 'SCF-AIG-012', ref: 'Annex A.10.4' }, // primary
  ],
};

module.exports = {
  FRAMEWORK_ISO42001,
};
